import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  Security as SecurityIcon,
  AccountCircle as AccountCircleIcon,
  Warning as WarningIcon,
  ContactSupport as ContactSupportIcon
} from '@mui/icons-material';

const TermsOfServicePage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Stack spacing={4}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h1" gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>

          <Divider />

          {/* Introduction */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Agreement to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing and using Life Runbooks ("Service"), you accept and agree to be bound by 
              the terms and provision of this agreement. If you do not agree to abide by the above, 
              please do not use this service.
            </Typography>
          </Box>

          {/* Description of Service */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              Life Runbooks is a web and mobile application that helps families and individuals 
              create, organize, and share emergency procedures, step-by-step guides, and important 
              information for various life situations.
            </Typography>
            <Typography variant="body1">
              The Service includes:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Runbook Creation and Management"
                  secondary="Tools to create and organize emergency procedures and guides"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Family Collaboration"
                  secondary="Features to share runbooks with family members"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Template Library"
                  secondary="Pre-built templates and AI-powered generation tools"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Mobile Access"
                  secondary="Cross-platform access to your runbooks"
                />
              </ListItem>
            </List>
          </Box>

          {/* User Accounts */}
          <Box>
            <Typography variant="h5" gutterBottom>
              User Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              When you create an account with us, you must provide accurate and complete information. 
              You are responsible for safeguarding your account credentials and for all activities 
              that occur under your account.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Account Security"
                  secondary="You must keep your password secure and notify us of any unauthorized use"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Account Responsibility"
                  secondary="You are responsible for all activities under your account"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Account Termination"
                  secondary="We reserve the right to terminate accounts that violate these terms"
                />
              </ListItem>
            </List>
          </Box>

          {/* Acceptable Use */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. 
              You agree not to use the Service:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="Illegal Activities"
                  secondary="In any way that violates any applicable federal, state, local, or international law"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Harmful Content"
                  secondary="To transmit or create content that is harmful, threatening, abusive, or defamatory"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Security Violations"
                  secondary="To attempt to gain unauthorized access to any part of the Service or other systems"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Service Interference"
                  secondary="To interfere with or disrupt the Service or servers connected to the Service"
                />
              </ListItem>
            </List>
          </Box>

          {/* Content and Intellectual Property */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Content and Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              You retain ownership of the content you create and upload to the Service. By using the Service, 
              you grant us a limited license to store, process, and display your content as necessary to 
              provide the Service.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemText 
                  primary="Your Content"
                  secondary="You own and are responsible for the content you create"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Our Service"
                  secondary="The Service and its original content are owned by Life Runbooks"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Third-Party Content"
                  secondary="We are not responsible for content provided by third parties"
                />
              </ListItem>
            </List>
          </Box>

          {/* Privacy and Data */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Privacy and Data Protection
            </Typography>
            <Typography variant="body1" paragraph>
              Your privacy is important to us. Our collection and use of personal information is 
              governed by our Privacy Policy, which is incorporated into these Terms by reference.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Data Security"
                  secondary="We implement appropriate security measures to protect your data"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Data Processing"
                  secondary="We process your data only as necessary to provide the Service"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Data Retention"
                  secondary="We retain your data only as long as necessary for the Service"
                />
              </ListItem>
            </List>
          </Box>

          {/* Disclaimers */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Disclaimers
            </Typography>
            <Typography variant="body1" paragraph>
              The Service is provided "as is" and "as available" without warranties of any kind. 
              We do not guarantee that the Service will be uninterrupted, secure, or error-free.
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="No Warranty"
                  secondary="We disclaim all warranties, express or implied"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Emergency Use"
                  secondary="The Service is not a substitute for professional emergency services"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Accuracy"
                  secondary="We do not guarantee the accuracy of user-generated content"
                />
              </ListItem>
            </List>
          </Box>

          {/* Limitation of Liability */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              In no event shall Life Runbooks be liable for any indirect, incidental, special, 
              consequential, or punitive damages arising out of or relating to your use of the Service.
            </Typography>
            <Typography variant="body1">
              Our total liability to you for any claims arising from these Terms or your use of the 
              Service shall not exceed the amount you paid us in the 12 months preceding the claim.
            </Typography>
          </Box>

          {/* Termination */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for any reason, including breach of these Terms.
            </Typography>
            <Typography variant="body1">
              Upon termination, your right to use the Service will cease immediately. We may delete 
              your account and data, though we will provide reasonable notice before permanent deletion.
            </Typography>
          </Box>

          {/* Governing Law */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed by and construed in accordance with the laws of the 
              jurisdiction in which Life Runbooks operates, without regard to conflict of law principles.
            </Typography>
          </Box>

          {/* Changes to Terms */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to modify these Terms at any time. We will notify users of any 
              material changes by posting the new Terms on this page and updating the "Last updated" date.
            </Typography>
            <Typography variant="body1">
              Your continued use of the Service after any changes constitutes acceptance of the updated Terms.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography variant="h5" gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about these Terms of Service, please contact us:
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ContactSupportIcon color="primary" />
              <Typography variant="body1">
                Email: legal@liferunbooks.com
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              We will respond to your inquiry within 30 days.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default TermsOfServicePage;